import React, { useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, Mail, LogOut, Clock, Users } from 'lucide-react';

export function InactiveOverlay() {
  const { user, logout } = useContext(AuthContext);

  return (
    <div
      data-testid="inactive-overlay"
      className="min-h-screen bg-bundle-cream flex items-center justify-center p-4"
    >
      <div className="max-w-lg w-full">
        <Card className="border-2 border-bundle-gold bg-white shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-bundle p-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Clock className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white">
              Trial Period Ended
            </h1>
            <p className="font-body text-bundle-cream/80 text-sm mt-1">
              Your 60-day free trial has expired
            </p>
          </div>

          <CardContent className="p-6 md:p-8">
            {/* Status message */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-body text-sm text-amber-800 font-medium">
                    Your membership is currently inactive
                  </p>
                  <p className="font-body text-xs text-amber-700 mt-1">
                    To regain full access to all Bundle Golf Club features, please contact the club admin to set up your membership.
                  </p>
                </div>
              </div>
            </div>

            {/* What you're missing */}
            <div className="mb-6">
              <h3 className="font-heading text-sm font-semibold text-bundle-green mb-3 uppercase tracking-wide">
                Reactivate to access
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  'Live Scorecard',
                  'Leaderboards',
                  'Bundle Stories',
                  'Club Events',
                  'Member Chat',
                  'Golf Trips',
                ].map((feature) => (
                  <div
                    key={feature}
                    className="flex items-center gap-2 text-gray-500 text-sm font-body"
                  >
                    <div className="w-1.5 h-1.5 bg-bundle-gold rounded-full" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Admin CTA */}
            <a
              data-testid="contact-admin-btn"
              href="mailto:admin@thebundlegolfclubusa.com?subject=Membership%20Reactivation%20Request&body=Hi%2C%20I%27d%20like%20to%20reactivate%20my%20Bundle%20Golf%20Club%20membership.%20My%20account%20email%20is%20"
              className="w-full"
            >
              <Button className="w-full btn-bundle py-5 text-base">
                <Mail className="w-5 h-5 mr-2" />
                Contact Admin to Reactivate
              </Button>
            </a>

            <p className="font-body text-xs text-center text-gray-500 mt-3">
              Email: admin@thebundlegolfclubusa.com
            </p>

            {/* Divider */}
            <div className="border-t border-gray-200 my-5" />

            {/* User info + Logout */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="font-body text-sm text-gray-500">
                  {user?.name || user?.email}
                </span>
              </div>
              <Button
                data-testid="inactive-logout-btn"
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-gray-500 hover:text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
